import { AxiosError } from "axios";
import { Mutation, Query } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { ILoginResponse } from "src/api/types";
import { buildLogoutUrl, captureDestination } from "src/services/loginEntry";

/*  At the heart of this file, there is an article on how to implement this refreshToken logic.
    https://dev.to/elmehdiamlou/efficient-refresh-token-implementation-with-react-query-and-axios-f8d
*/

type IErrorResponse = {
    message: string;
};
export const refreshTokenHandler = (
    error: unknown,
    query?: Query,
    mutation?: Mutation<unknown, unknown, unknown, unknown>,
    variables?: unknown,
) => {
    const resp = (error as AxiosError<IErrorResponse>).response;
    if (!resp) {
        // Network error or unexpected shape; let global handlers/logging manage it
        console.error(error);
        return;
    }
    const { status, data } = resp;

    if (status === 401) {
        if (mutation) refreshTokenAndRetry(undefined, mutation, variables);
        else refreshTokenAndRetry(query);
    } else console.error(data?.message);
};

const refreshTokenUrl = "/api/token/refresh" as const;

export const refreshAccessTokenFn = async () => {
    // Since we're using Cookies, it will automatically reset the Cookie.
    await loggedInApiCall.post<ILoginResponse>(refreshTokenUrl);
};

type FailedQueue = {
    query?: Query;
    mutation?: Mutation<unknown, unknown, unknown, unknown>;
    variables?: unknown;
};

let isRefreshing = false;
let failedQueue: FailedQueue[] = [];
const refreshTokenAndRetry = async (
    query?: Query,
    mutation?: Mutation<unknown, unknown, unknown, unknown>,
    variables?: unknown,
) => {
    try {
        if (isRefreshing) {
            failedQueue.push({ query, mutation, variables });
            return;
        }

        isRefreshing = true;
        failedQueue.push({ query, mutation, variables });
        await refreshAccessTokenFn();
        processFailedQueue();
    } catch {
        // This means a refresh token has failed.
        // Redirect to login, telling it why and where the user was;
        const url = buildLogoutUrl({
            reason: "expired",
            destination: captureDestination(window.location),
        });
        window.location.href = url;
    }
};

const processFailedQueue = () => {
    failedQueue.forEach(({ query, mutation }) => {
        if (mutation) {
            mutation.execute();
        }
        if (query) {
            query.fetch();
        }
    });
    isRefreshing = false;
    failedQueue = [];
};
